import type { Workspace, WorkspaceNarrowType } from '@carboncopyco/types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  selectOrganizationId,
  useGetCurrentUserQuery,
  useGetOrganizationQuery,
  useInfiniteScroll,
  useInviteToWorkspaceMutation,
  useLazyGetRolesQuery,
  useLazyGetWorkspaceMembersQuery,
  useRemoveFromWorkspaceMutation,
} from '@carboncopyco/data';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { useWorkspaceAnalytics } from '@carboncopyco/utils';
import { usePermissionsContext } from '../../utils';
import { WorkspaceMembersModal } from '../WorkspaceMembersModal';
import { useMembersModalFormAnalytics } from './helpers';

export type WorkspaceMembersModalWrapperDataLayerProps = {
  workspace: Workspace;
  show: boolean;
  onClose: (workspace?: Workspace) => void;
  onInvite: (emails: string[]) => void;
  onRemove: () => void;
};

export const WorkspaceMembersModalWrapper = ({
  workspace,
  show,
  onClose,
  onInvite,
  onRemove,
}: WorkspaceMembersModalWrapperDataLayerProps) => {
  const { getPermissions } = usePermissionsContext();
  const {
    items,
    isLoading: isLoadingMembers,
    isLoadingBatch: isLoadingMembersBatch,
    error: errorGettingMembers,
    fetchFirstBatch: membersFetchFirstBatch,
    fetchNextBatch: membersFetchNextBatch,
  } = useInfiniteScroll(useLazyGetWorkspaceMembersQuery, {
    preferCacheValue: false,
  });
  const members = items || [];
  const removeMemberPermission = getPermissions(
    PermissionKey.workspaceMember,
    PermissionAction.removeMemberFromWorkspace
  );
  const organizationId = useSelector(selectOrganizationId);
  const { data: organization } = useGetOrganizationQuery({
    id: organizationId,
  });
  const {
    items: roles,
    fetchFirstBatch: rolesFetchFirstBatch,
    fetchNextBatch: rolesFetchNextBatch,
    nextBatchCursor,
  } = useInfiniteScroll(useLazyGetRolesQuery);

  const organizationName = organization?.organizationName;
  const { id: workspaceId, workspaceType } = workspace;
  const { data: userData } = useGetCurrentUserQuery();
  const { id: userId } = userData?.user || {};
  const [inviteMembers, { isLoading: isSendingInvites }] =
    useInviteToWorkspaceMutation();
  const [removeMember] = useRemoveFromWorkspaceMutation();

  useEffect(() => {
    membersFetchFirstBatch({ organizationId, workspaceId });
    if (!roles) {
      rolesFetchFirstBatch({ organizationId, limit: 100 });
    }
  }, [workspaceId]);

  useEffect(() => {
    if (nextBatchCursor) {
      rolesFetchNextBatch({
        organizationId,
        limit: 100,
        nextBatchCursor,
      });
    }
  }, [nextBatchCursor]);

  const organizationEmails = roles?.map((role) => role.user?.email);

  useEffect(() => {
    if (errorGettingMembers) {
      notify.error(
        "Error fetching workspace members. Can't show members modal"
      );
    }
  }, [errorGettingMembers]);
  const { trackWorkspaceClickAddMember, trackWorkspaceClickRemoveMember } =
    useWorkspaceAnalytics('workspace_members_modal');
  const handleFormErrorAnalytics = useMembersModalFormAnalytics();

  const handleInviteMembers = (emails: string[]) => {
    inviteMembers({ organizationId, workspaceId, emails })
      .unwrap()
      .then((response) => {
        onInvite(emails);
        membersFetchFirstBatch({
          organizationId,
          workspaceId,
        });
        trackWorkspaceClickAddMember({ workspace });

        if (response?.length < emails.length) {
          notify.warning(
            'There was a problem inviting some organization members to the workspace'
          );
          return;
        }
        notify.success(
          'Organization members invited to workspace successfully'
        );
      })
      .catch(() => {
        notify.error('Error inviting organization members to workspace');
      });
  };

  const handleRemoveMember = (memberId: string) => {
    const member = members.find((member) => member.id === memberId);
    const memberName = member?.user?.name;
    removeMember({
      organizationId,
      workspaceId,
      memberId,
      options: { preventInvalidate: true },
    })
      .unwrap()
      .then(() => {
        onRemove();
        membersFetchFirstBatch({
          organizationId,
          workspaceId,
        });
        trackWorkspaceClickRemoveMember({
          workspace,
          workspaceMember: member,
        });
        notify.success(
          `${memberName} has been removed from workspace successfully`
        );
      })
      .catch(() => {
        notify.error(`Error removing ${memberName} from workspace`);
      });
  };

  if (
    workspace.workspaceType === 'default' ||
    workspace.workspaceType === 'playground'
  ) {
    return null;
  }

  return (
    <WorkspaceMembersModal
      isSendingInvites={isSendingInvites}
      members={members}
      isOpen={show}
      inviteMembers={handleInviteMembers}
      handleClose={() => onClose(workspace)}
      type={workspaceType as WorkspaceNarrowType}
      userId={userId || ''}
      removeMember={handleRemoveMember}
      fetchMoreMembers={() => {
        membersFetchNextBatch({
          organizationId,
          workspaceId,
        });
      }}
      isLoadingMembers={isLoadingMembers}
      isLoadingMoreMembers={isLoadingMembersBatch}
      organizationName={organizationName || ''}
      organizationMembers={organizationEmails || []}
      showRowDropdown={removeMemberPermission}
      handleFormErrorAnalytics={handleFormErrorAnalytics}
    />
  );
};
